/**
 * Shared Firebase Auth bootstrap for the multi-project setup.
 *
 * Both SPAs (`apps/app` advisors, `apps/vault` owners + heirs) authenticate
 * against *different* Firebase projects. This module centralises:
 *
 *  - an **empty-aware** config coalesce (`coalesce`) — a present-but-blank env
 *    var (e.g. `VITE_VAULT_FIREBASE_API_KEY=`) must NOT win over the fallback, or
 *    Firebase bakes `apiKey: ""` and throws `auth/invalid-api-key` in prod;
 *  - `initFirebaseAuth` — `initializeApp` + `getAuth` + optional emulator wiring.
 *
 * It is deliberately framework-agnostic (no React) so it can be shared. Each app
 * builds its own `FirebaseWebConfig` (from its own `VITE_*` vars) and passes it in.
 */
import { type FirebaseApp, initializeApp } from "firebase/app";
import { type Auth, connectAuthEmulator, getAuth } from "firebase/auth";

export interface FirebaseWebConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket?: string;
  messagingSenderId?: string;
  appId?: string;
}

/**
 * Return `primary` unless it is null/undefined/blank, in which case return
 * `fallback`. This is the `??`-vs-empty-string fix: `??` keeps a present-but-empty
 * string, which is exactly the failure mode that white-screens the app in prod.
 */
export function coalesce(
  primary: string | undefined,
  fallback: string | undefined
): string | undefined {
  return primary && primary.trim() !== "" ? primary : fallback;
}

/** Strip surrounding quotes that some deploy pipelines add to env values. */
function stripQuotes(v: string | undefined): string | undefined {
  if (typeof v !== "string") return v;
  return v.startsWith('"') && v.endsWith('"') ? v.slice(1, -1) : v;
}

export interface InitFirebaseArgs {
  /** Unique app name so two SPAs in one page context never collide. */
  name: string;
  config: FirebaseWebConfig;
  /** True only during local `vite dev`. */
  useEmulator?: boolean;
  /** e.g. `127.0.0.1:9098` for the vault emulator. */
  emulatorHost?: string;
}

export interface FirebaseHandles {
  app: FirebaseApp;
  auth: Auth;
}

export function initFirebaseAuth({
  name,
  config,
  useEmulator = false,
  emulatorHost,
}: InitFirebaseArgs): FirebaseHandles {
  const cleaned: FirebaseWebConfig = {
    apiKey: stripQuotes(config.apiKey) ?? "",
    authDomain: stripQuotes(config.authDomain) ?? "",
    projectId: stripQuotes(config.projectId) ?? "",
    storageBucket: stripQuotes(config.storageBucket),
    messagingSenderId: stripQuotes(config.messagingSenderId),
    appId: stripQuotes(config.appId),
  };

  for (const key of ["apiKey", "authDomain", "projectId"] as const) {
    if (!cleaned[key]) {
      console.error(`[firebase-auth] Missing required config: ${key}`);
    }
  }

  const app = initializeApp(cleaned, name);
  const auth = getAuth(app);

  if (useEmulator && emulatorHost) {
    connectAuthEmulator(auth, `http://${emulatorHost}`, {
      disableWarnings: true,
    });
    // eslint-disable-next-line no-console
    console.info(
      `[firebase-auth] '${name}' connected to Auth emulator ${emulatorHost} (project ${cleaned.projectId})`
    );
  }

  return { app, auth };
}
